/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-26
 */

// Add third-party dependencies.
import { Badge, useTheme } from '@mui/material';

/**
 * Props for the TabBadge component.
 */
interface TabBadgeProps {
  /**
   * The notification count to display.
   */
  count: number;
  /**
   * The child element to wrap with the badge.
   */
  children: React.ReactNode;
  /**
   * Maximum count to display before showing "99+".
   * @default 99
   */
  max?: number;
}

/**
 * Badge component for tab notifications.
 * Displays notification count on tab icons with proper styling.
 *
 * @param props - The component props
 * @returns {React.ReactElement} The tab badge component
 *
 * @example
 * return (
 *   <TabBadge count={5}>
 *     <DashboardIcon />
 *   </TabBadge>
 * );
 */
const TabBadge: React.FC<TabBadgeProps> = ({ count, children, max = 99 }) => {
  const theme = useTheme();

  // Don't show badge if count is 0 or negative
  if (count <= 0) {
    return children as React.ReactElement;
  }

  return (
    <Badge
      badgeContent={count}
      max={max}
      sx={{
        '& .MuiBadge-badge': {
          backgroundColor: theme.palette.error.main,
          color: theme.palette.error.contrastText,
          fontSize: '0.625rem',
          fontWeight: 600,
          minWidth: 16,
          height: 16,
          padding: '0 4px',
          // Position slightly higher for better visibility
          top: 2,
          right: 2,
          // Ensure it's visible on touch devices
          zIndex: 1,
        },
      }}
    >
      {children}
    </Badge>
  );
};

export default TabBadge;
